import React, { useState, useRef, useEffect, useCallback } from 'react';
import './otp.css';
import { Box, Container, Grid, Paper, Typography, Alert, Collapse } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useAsociadoStore } from '@store/useAsociadoStore';
import { InterInstance } from '@api/config';
import { getToken } from '@api/getToken';
import { useTokenStore } from '@store/TokenStore';
import { usePolicyStore } from '@store/policyStore';
import { useIdStore } from '@store/UrlIdStore';

// La función debounce se mantiene para optimizar la entrada del usuario
const debounce = <F extends (...args: any[]) => any>(func: F, waitFor: number) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<F>): void => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), waitFor);
  };
};

const OTP: React.FC = () => {
  // --- ESTADOS Y HOOKS INTERNOS ---
  const [otp, setOtp] = useState<string[]>(new Array(5).fill(''));
  const [tiempoRestante, setTiempoRestante] = useState<number>(180);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const history = useHistory();
  const { setToken, token } = useTokenStore();
  const { policy, addPolicyInfo} =usePolicyStore();
  const { id, setId } = useIdStore();



  // Obtenemos los datos del usuario directamente del store
  const { datos, marcarVerificado, guardarDatos,resetear } = useAsociadoStore();
  const recipientEmail = datos?.email || 'tu correo'; // Usamos el email del store

  //verificacion de empleado
  const verifyCoppelEmployeeId = async (employeeId: string, email: string) => {
    let tmpToken = token;
    if (!tmpToken) {
      tmpToken = await getToken();
    }
    const { data } = await InterInstance({
      method: 'GET',
      url: `/catalogs/coppel-workers/buscar/${employeeId}/${email}`,
      headers: { Authorization: `Bearer ${tmpToken}` },
    }).catch((error) => {
      console.log(error);
      //setLoading(false)
      return error;
    });

      if (data.success && data.allowed) {
        const info = data.data;

        addPolicyInfo({
          ...policy,
          employee_id: info.NumeroColaborador,
          //email: info.correo,

          employee_obj: info,
        });

        return true;
      }


    return false;
  };


  const session = async (employeedeid: string, email: string) => {
   // alert('entre==>>>1');
    if (policy?.employee_id === '') {
     // alert('entre==>>>')
      let response = await verifyCoppelEmployeeId(employeedeid, email);
      if (response) {
        history.push('/customer-portal');
        setIsLoading(false);
        // resetear()
      } else {
        setIsLoading(false);
        history.replace({ pathname: '/' });
        //resetear()
      }
    }
  };
  // --- LÓGICA DE NEGOCIO INTERNA ---

  // Función para verificar el OTP contra el backend
  const handleVerifyOtp = async (code: string) => {
    if (!datos?.numColaborador) {
      setError('No se encontró el número de colaborador para la verificación.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const token = await getToken();
      // Asumimos un endpoint de verificación de OTP
      const response = await InterInstance.post(
        //servicio de otp
        '/catalogs/coppel-workers/validarcodigo',
        {
          idColaborador: datos.numColaborador,
          codigo: code,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data && response.data.success) {
       // console.log('Respuesta del API otp==>>:', response.data);
        if(datos?.email){
        //  console.log('Respuesta del API otp==>>:', response.data);
          await session(datos.numColaborador, datos?.email);
        }
       // console.log('Email datos del API otp==>>:', datos?.email);
      } else {
        setError(response.data.message || 'El código ingresado no es válido.');
        setOtp(new Array(5).fill('')); // Limpiamos los campos en caso de error
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      setError('Ocurrió un error al verificar el código. Inténtalo de nuevo.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Función para solicitar un nuevo código
  const handleResendCode = async () => {
    setTiempoRestante(180);
   // console.log('Reenviando código...');
    // Aquí iría la lógica para llamar a tu API y reenviar el SMS/email
     try {
    const response = await InterInstance.get(
      `/catalogs/coppel-workers/envianotificacion/${datos?.numColaborador}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    //console.log('Respuesta del API:', response);
    if (response?.data && response.data.success && response.data.allowed) {
     // console.log('data==>>>', response.data.data)

      const responseData = {
        ...datos,
        ...response.data.data,
        numColaborador: datos?.numColaborador,
      };
      await guardarDatos(responseData);
      await marcarVerificado(true);

    } else {
      setError('El número de colaborador no es válidos.');
    }
  } catch (error) {
    setError('Ocurrió un error al validar el usuario. Por favor, inténtalo de nuevo.');
    console.error('Error al validar el usuario:', error);
  } finally {
    setIsLoading(false);
  }
  };

  // Usamos debounce para llamar a la verificación
  const debouncedVerify = useCallback(debounce(handleVerifyOtp, 500), [
    datos,
  ]);

  // --- MANEJADORES DE EVENTOS Y EFECTOS ---

  useEffect(() => {
    if (tiempoRestante > 0) {
      const timerId = setTimeout(
        () => setTiempoRestante(tiempoRestante - 1),
        1000
      );
      return () => clearTimeout(timerId);
    }
  }, [tiempoRestante]);

  const formatoTiempo = (): string => {
    const minutos = Math.floor(tiempoRestante / 60);
    const segundos = tiempoRestante % 60;
    return `${minutos}:${segundos.toString().padStart(2, '0')}`;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const valor = e.target.value;
    if (isNaN(Number(valor))) return;

    const nuevoOtp = [...otp];
    nuevoOtp[index] = valor.substring(valor.length - 1);
    setOtp(nuevoOtp);

    if (valor && index < 4) {
      inputRefs.current[index + 1]?.focus();
    }

    const codigoCompleto = nuevoOtp.join('');
    if (codigoCompleto.length === 5 && !nuevoOtp.includes('')) {
      debouncedVerify(codigoCompleto);
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };
  useEffect(() => {
    if(datos?.numColaborador){
      setId(datos?.numColaborador);
    }
  }, [datos]);

  // --- RENDERIZADO DEL COMPONENTE ---
  return (
    <Box sx={{ py: { xs: 6, md: 25 } }}>
      <Container maxWidth="lg">
        <Grid container justifyContent={'center'} alignItems={'center'}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: '16px',
              border: '1px solid #EAEAEA',
              boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
              backgroundColor: '#FFFFFF',
              width: { xs: '285px', md: '410px' },
            }}
          >
            <div className="confirmacion-container">
              <h2 className="confirmacion-titulo">
                Ayúdanos a confirmar que eres tú
              </h2>
              <Collapse in={!!error}>
                <Alert
                  severity="error"
                  onClose={() => setError(null)}
                  sx={{ mb: 2 }}
                >
                  {error}
                </Alert>
              </Collapse>
              <p className="confirmacion-descripcion">
                Enviamos un <strong>código de autenticación</strong> a tu
                correo:
              </p>
              <Typography sx={{ fontWeight: 'bold', mb: 2 }}>
                {recipientEmail}
              </Typography>

              <div className="otp-inputs-wrapper">
                {otp.map((digito, index) => (
                  <input
                    key={index}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digito}
                    placeholder="-"
                    className="otp-caja"
                    onChange={(e) => handleChange(e, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    ref={(el) => (inputRefs.current[index] = el)}
                    onFocus={(e) => e.target.select()}
                    disabled={isLoading}
                  />
                ))}
              </div>

              <div className="reenviar-seccion">
                {tiempoRestante > 0 ? (
                  <span className="reenviar-texto inactivo">
                    Solicitar un nuevo código en {formatoTiempo()} minutos
                  </span>
                ) : (
                  <button
                    className="reenviar-btn"
                    onClick={handleResendCode}
                    disabled={isLoading}
                  >
                    Solicitar un nuevo código
                  </button>
                )}
              </div>
            </div>
          </Paper>
        </Grid>
      </Container>
    </Box>
  );
};

export default OTP;
